import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Card, Grid} from 'semantic-ui-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API } from '../../helpers';
import { renderQuota } from '../../helpers/render';
import './Dashboard.css';

// 在 Dashboard 组件内添加自定义配置
const chartConfig = {
  lineChart: {
    style: {
      background: '#fff',
      borderRadius: '8px',
    },
    line: {
      strokeWidth: 2,
      dot: false,
      activeDot: { r: 4 },
    },
    grid: {
      vertical: false,
      horizontal: true,
      opacity: 0.1,
    },
  },
  colors: {
    requests: '#4318FF',
    quota: '#00B5D8',
    tokens: '#6C63FF',
  },
  barColors: [
    '#4318FF', // 深紫色
    '#00B5D8', // 青色
    '#6C63FF', // 紫色
    '#05CD99', // 绿色
    '#FFB547', // 橙色
    '#FF5E7D', // 粉色
    '#41B883', // 翠绿
    '#7983FF', // 淡紫
    '#FF8F6B', // 珊瑚色
    '#49BEFF', // 天蓝
  ],
};

const Dashboard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    todayRequests: 0,
    todayQuota: 0,
    todayTokens: 0,
  });
  // P4：订阅汇总（{ subscriptions:[{subscription,plan,days_remaining}], models_union, unlimited_models, trial_days_remaining }）
  const [subSummary, setSubSummary] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchSubscriptionSummary();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res = await API.get('/api/user/dashboard');
      if (res.data.success) {
        const dashboardData = Array.isArray(res.data.data) ? res.data.data : [];
        setData(dashboardData);
        calculateSummary(dashboardData);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setData([]);
      calculateSummary([]);
    }
  };

  const fetchSubscriptionSummary = async () => {
    try {
      const res = await API.get('/api/subscription/me');
      if (res.data.success && res.data.data) {
        const d = res.data.data;
        // 归一化：无订阅时后端返回空数组而非 null
        if (
          (Array.isArray(d.subscriptions) && d.subscriptions.length > 0) ||
          (d.trial_days_remaining || 0) > 0
        ) {
          setSubSummary(d);
        } else {
          setSubSummary(null);
        }
      }
    } catch (e) {
      // 静默：订阅汇总失败不阻塞 Dashboard
    }
  };

  const calculateSummary = (dashboardData) => {
    if (!Array.isArray(dashboardData) || dashboardData.length === 0) {
      setSummaryData({ todayRequests: 0, todayQuota: 0, todayTokens: 0 });
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    const todayData = dashboardData.filter((item) => item && item.Day === today);

    const summary = {
      todayRequests: todayData.reduce(
        (sum, item) => sum + (item.RequestCount || 0),
        0
      ),
      todayQuota:
        todayData.reduce((sum, item) => sum + (item.Quota || 0), 0) / 1000000,
      todayTokens: todayData.reduce(
        (sum, item) => sum + (item.PromptTokens || 0) + (item.CompletionTokens || 0),
        0
      ),
    };

    setSummaryData(summary);
  };

  // 处理数据以供折线图使用，补充缺失的日期
  const processTimeSeriesData = () => {
    try {
      if (!Array.isArray(data) || data.length === 0) return [];
      const dailyData = {};

      // 获取日期范围
      const dates = data.map((item) => item.Day);
      const maxDate = new Date(); // 总是使用今天作为最后一天
      let minDate =
        dates.length > 0
          ? new Date(Math.min(...dates.map((d) => new Date(d))))
          : new Date();

      // 确保至少显示7天的数据
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6); // -6是因为包含今天
      if (minDate > sevenDaysAgo) {
        minDate = sevenDaysAgo;
      }

      // 生成所有日期
      for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        dailyData[dateStr] = {
          date: dateStr,
          requests: 0,
          quota: 0,
          tokens: 0,
        };
      }

      // 填充实际数据
      data.forEach((item) => {
        if (!item || !item.Day || !dailyData[item.Day]) return;
        dailyData[item.Day].requests += (item.RequestCount || 0);
        dailyData[item.Day].quota += (item.Quota || 0) / 1000000;
        dailyData[item.Day].tokens += (item.PromptTokens || 0) + (item.CompletionTokens || 0);
      });

      return Object.values(dailyData).sort((a, b) =>
        a.date.localeCompare(b.date)
      );
    } catch (e) {
      console.error('processTimeSeriesData error:', e);
      return [];
    }
  };

  // 处理数据以供堆叠柱状图使用
  const processModelData = () => {
    try {
      if (!Array.isArray(data) || data.length === 0) return [];
      const timeData = {};

      // 获取日期范围
      const dates = data.map((item) => item.Day);
      const maxDate = new Date(); // 总是使用今天作为最后一天
      let minDate =
        dates.length > 0
          ? new Date(Math.min(...dates.map((d) => new Date(d))))
          : new Date();

      // 确保至少显示7天的数据
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6); // -6是因为包含今天
      if (minDate > sevenDaysAgo) {
        minDate = sevenDaysAgo;
      }

      // 生成所有日期
      for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        timeData[dateStr] = {
          date: dateStr,
        };

        // 初始化所有模型的数据为0
        const models = [...new Set(data.map((item) => item.ModelName))];
        models.forEach((model) => {
          timeData[dateStr][model] = 0;
        });
      }

      // 填充实际数据
      data.forEach((item) => {
        if (!item || !item.Day || !timeData[item.Day]) return;
        timeData[item.Day][item.ModelName] =
          (item.PromptTokens || 0) + (item.CompletionTokens || 0);
      });

      return Object.values(timeData).sort((a, b) => a.date.localeCompare(b.date));
    } catch (e) {
      console.error('processModelData error:', e);
      return [];
    }
  };

  // 获取所有唯一的模型名称
  const getUniqueModels = () => {
    return [...new Set(data.map((item) => item.ModelName))];
  };

  const timeSeriesData = processTimeSeriesData();
  const modelData = processModelData();
  const models = getUniqueModels();

  // 生成随机颜色
  const getRandomColor = (index) => {
    return chartConfig.barColors[index % chartConfig.barColors.length];
  };

  // 添加一个日期格式化函数
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      month: 'numeric',
      day: 'numeric',
    });
  };

  // 修改所有 XAxis 配置
  const xAxisConfig = {
    dataKey: 'date',
    axisLine: false,
    tickLine: false,
    tick: {
      fontSize: 12,
      fill: '#A3AED0',
      textAnchor: 'middle', // 文本居中对齐
    },
    tickFormatter: formatDate,
    interval: 0,
    minTickGap: 5,
    padding: { left: 30, right: 30 }, // 增加两侧的内边距，确保首尾标签完整显示
  };

  // P4：订阅汇总卡数据加工
  const subs =
    subSummary && Array.isArray(subSummary.subscriptions)
      ? subSummary.subscriptions
      : [];
  const planNames = subs.map((s) => s.plan?.name).filter(Boolean);
  const maxDays = subs.reduce(
    (m, s) => Math.max(m, s.days_remaining || 0),
    0
  );
  const quotaGroups = Array.isArray(subSummary?.quota_groups)
    ? subSummary.quota_groups
    : [];
  const totalQuota = quotaGroups.reduce((s, g) => s + (g.total || 0), 0);
  const usedQuota = quotaGroups.reduce((s, g) => s + (g.used || 0), 0);
  const ownedModels = subSummary?.unlimited_models
    ? null
    : Array.isArray(subSummary?.models_union)
    ? subSummary.models_union
    : [];
  const trialDays = subSummary?.trial_days_remaining || 0;

  return (
    <div className='dashboard-container'>
      {/* 订阅/试用汇总卡 */}
      {subSummary && (
        <Card fluid className='chart-card' style={{ marginBottom: '1em' }}>
          <Card.Content>
            <Grid columns={4} stackable doubling divided>
              <Grid.Column textAlign='center'>
                <div style={{ color: '#A3AED0', fontSize: 12 }}>
                  {t('dashboard.subscription.plan')}
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>
                  {planNames.length > 0
                    ? planNames.join(' + ')
                    : t('dashboard.subscription.no_plan')}
                </div>
              </Grid.Column>
              <Grid.Column textAlign='center'>
                <div style={{ color: '#A3AED0', fontSize: 12 }}>
                  {t('dashboard.subscription.days_remaining')}
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>
                  {subs.length > 0
                    ? t('dashboard.subscription.days_value', {
                        days: maxDays,
                      })
                    : trialDays > 0
                    ? t('dashboard.subscription.trial_value', {
                        days: trialDays,
                      })
                    : '-'}
                </div>
              </Grid.Column>
              <Grid.Column textAlign='center'>
                <div style={{ color: '#A3AED0', fontSize: 12 }}>
                  {t('dashboard.subscription.quota_used')}
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>
                  {totalQuota > 0
                    ? `${renderQuota(usedQuota, t)} / ${renderQuota(
                        totalQuota,
                        t
                      )}`
                    : t('dashboard.subscription.unlimited')}
                </div>
              </Grid.Column>
              <Grid.Column textAlign='center'>
                <div style={{ color: '#A3AED0', fontSize: 12 }}>
                  {t('dashboard.subscription.models')}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    marginTop: 4,
                    wordBreak: 'break-word',
                  }}
                >
                  {subSummary?.unlimited_models
                    ? t('dashboard.subscription.unlimited')
                    : ownedModels.length > 0
                    ? ownedModels.join(', ')
                    : '-'}
                </div>
              </Grid.Column>
            </Grid>
          </Card.Content>
        </Card>
      )}

      {/* 三个并排的折线图 */}
      <Grid columns={3} stackable className='charts-grid'>
        <Grid.Column>
          <Card fluid className='chart-card'>
            <Card.Content>
              <Card.Header>
                {t('dashboard.charts.requests.title')}
                {/* <span className='stat-value'>{summaryData.todayRequests}</span> */}
              </Card.Header>
              <div className='chart-container'>
                <ResponsiveContainer
                  width='100%'
                  height={120}
                  margin={{ left: 10, right: 10 }} // 调整容器边距
                >
                  <LineChart data={timeSeriesData}>
                    <CartesianGrid
                      strokeDasharray='3 3'
                      vertical={chartConfig.lineChart.grid.vertical}
                      horizontal={chartConfig.lineChart.grid.horizontal}
                      opacity={chartConfig.lineChart.grid.opacity}
                    />
                    <XAxis {...xAxisConfig} />
                    <YAxis hide={true} />
                    <Tooltip
                      contentStyle={{
                        background: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                      formatter={(value) => [
                        value,
                        t('dashboard.charts.requests.tooltip'),
                      ]}
                      labelFormatter={(label) =>
                        `${t(
                          'dashboard.statistics.tooltip.date'
                        )}: ${formatDate(label)}`
                      }
                    />
                    <Line
                      type='monotone'
                      dataKey='requests'
                      stroke={chartConfig.colors.requests}
                      strokeWidth={chartConfig.lineChart.line.strokeWidth}
                      dot={chartConfig.lineChart.line.dot}
                      activeDot={chartConfig.lineChart.line.activeDot}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Content>
          </Card>
        </Grid.Column>

        <Grid.Column>
          <Card fluid className='chart-card'>
            <Card.Content>
              <Card.Header>
                {t('dashboard.charts.quota.title')}
                {/* <span className='stat-value'>
                  ${summaryData.todayQuota.toFixed(3)}
                </span> */}
              </Card.Header>
              <div className='chart-container'>
                <ResponsiveContainer
                  width='100%'
                  height={120}
                  margin={{ left: 10, right: 10 }} // 调整容器边距
                >
                  <LineChart data={timeSeriesData}>
                    <CartesianGrid
                      strokeDasharray='3 3'
                      vertical={chartConfig.lineChart.grid.vertical}
                      horizontal={chartConfig.lineChart.grid.horizontal}
                      opacity={chartConfig.lineChart.grid.opacity}
                    />
                    <XAxis {...xAxisConfig} />
                    <YAxis hide={true} />
                    <Tooltip
                      contentStyle={{
                        background: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                      formatter={(value) => [
                        value.toFixed(6),
                        t('dashboard.charts.quota.tooltip'),
                      ]}
                      labelFormatter={(label) =>
                        `${t(
                          'dashboard.statistics.tooltip.date'
                        )}: ${formatDate(label)}`
                      }
                    />
                    <Line
                      type='monotone'
                      dataKey='quota'
                      stroke={chartConfig.colors.quota}
                      strokeWidth={chartConfig.lineChart.line.strokeWidth}
                      dot={chartConfig.lineChart.line.dot}
                      activeDot={chartConfig.lineChart.line.activeDot}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Content>
          </Card>
        </Grid.Column>

        <Grid.Column>
          <Card fluid className='chart-card'>
            <Card.Content>
              <Card.Header>
                {t('dashboard.charts.tokens.title')}
                {/* <span className='stat-value'>{summaryData.todayTokens}</span> */}
              </Card.Header>
              <div className='chart-container'>
                <ResponsiveContainer
                  width='100%'
                  height={120}
                  margin={{ left: 10, right: 10 }} // 调整容器边距
                >
                  <LineChart data={timeSeriesData}>
                    <CartesianGrid
                      strokeDasharray='3 3'
                      vertical={chartConfig.lineChart.grid.vertical}
                      horizontal={chartConfig.lineChart.grid.horizontal}
                      opacity={chartConfig.lineChart.grid.opacity}
                    />
                    <XAxis {...xAxisConfig} />
                    <YAxis hide={true} />
                    <Tooltip
                      contentStyle={{
                        background: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                      formatter={(value) => [
                        value,
                        t('dashboard.charts.tokens.tooltip'),
                      ]}
                      labelFormatter={(label) =>
                        `${t(
                          'dashboard.statistics.tooltip.date'
                        )}: ${formatDate(label)}`
                      }
                    />
                    <Line
                      type='monotone'
                      dataKey='tokens'
                      stroke={chartConfig.colors.tokens}
                      strokeWidth={chartConfig.lineChart.line.strokeWidth}
                      dot={chartConfig.lineChart.line.dot}
                      activeDot={chartConfig.lineChart.line.activeDot}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Content>
          </Card>
        </Grid.Column>
      </Grid>

      {/* 模型使用统计 */}
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header>{t('dashboard.statistics.title')}</Card.Header>
          <div className='chart-container'>
            <ResponsiveContainer width='100%' height={300}>
              <BarChart data={modelData}>
                <CartesianGrid
                  strokeDasharray='3 3'
                  vertical={false}
                  opacity={0.1}
                />
                <XAxis {...xAxisConfig} />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#A3AED0' }}
                />
                <Tooltip
                  contentStyle={{
                    background: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                  labelFormatter={(label) =>
                    `${t('dashboard.statistics.tooltip.date')}: ${formatDate(
                      label
                    )}`
                  }
                />
                <Legend
                  wrapperStyle={{
                    paddingTop: '20px',
                  }}
                />
                {models.map((model, index) => (
                  <Bar
                    key={model}
                    dataKey={model}
                    stackId='a'
                    fill={getRandomColor(index)}
                    name={model}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

export default Dashboard;
